export const changeControlEn = {
  title: 'Change control',
  subtitle: 'Assess cross-module effects, coordinate reviews and create controlled revisions.',
  help: 'Open change-control help',
  tabs: { requests: 'Change requests', customerApprovals: 'Customer approvals' },
  actions: {
    new: 'New change request',
    analyze: 'Analyse impact',
    submit: 'Submit',
    reviewed: 'Review complete',
    'changes-requested': 'Request changes',
    approve: 'Approve',
    reject: 'Reject',
    'start-implementation': 'Start implementation',
    complete: 'Complete',
    cancel: 'Cancel request',
    archive: 'Archive',
  },
  filters: {
    search: 'Search number, title, source or product',
    allStatuses: 'All statuses',
    allRisks: 'All risk levels',
    allSources: 'All source types',
  },
  columns: {
    number: 'Number',
    title: 'Title',
    source: 'Source',
    status: 'Status',
    risk: 'Risk',
    responsible: 'Responsible',
    impact: 'Affected objects',
    updated: 'Updated',
    actions: 'Actions',
  },
  statuses: {
    DRAFT: 'Draft',
    IN_REVIEW: 'In review',
    CHANGES_REQUESTED: 'Changes requested',
    REVIEWED: 'Reviewed',
    APPROVED: 'Approved',
    IMPLEMENTING: 'In implementation',
    IMPLEMENTED: 'Implemented',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled',
    ARCHIVED: 'Archived',
  },
  risks: { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', CRITICAL: 'Critical' },
  sourceTypes: {
    PRODUCT: 'Product / material',
    MATERIAL_PROFILE: 'Material profile',
    SUPPLIER_SPECIFICATION: 'Supplier specification',
    RECIPE: 'Recipe / BOM',
    DECLARATION: 'Declaration',
    ANALYSIS_PROFILE: 'Analysis / nutrition',
    PROCESS_ROUTING: 'Process routing',
    PACKAGING: 'Packaging',
  },
  assigneeRoles: {
    RESPONSIBLE: 'Responsible',
    REVIEWER: 'Reviewer',
    APPROVER: 'Approver',
    OBSERVER: 'Observer',
  },
  taskStatuses: {
    OPEN: 'Open',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
  },
  decisions: {
    SUBMITTED: 'Submitted',
    REVIEWED: 'Reviewed',
    CHANGES_REQUESTED: 'Changes requested',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    IMPLEMENTING: 'Implementation started',
    IMPLEMENTED: 'Implemented',
    CANCELLED: 'Cancelled',
    ARCHIVED: 'Archived',
  },
  nodeTypes: {
    SOURCE: 'Change source',
    RECIPE: 'Recipes / BOMs',
    PRODUCT: 'Products',
    PRODUCTION_ORDER: 'Production orders',
    PRODUCTION_PLAN: 'Production planning',
    DECLARATION: 'Declarations',
    SPECIFICATION: 'Product specifications',
    LABEL_TEMPLATE: 'Label templates',
    CUSTOMER_APPROVAL: 'Customer approvals',
    SALES_ORDER: 'Open sales orders',
    PROCESS_ROUTING: 'Process routings',
    ANALYSIS_PROFILE: 'Analysis profiles',
    PACKAGING: 'Packaging',
  },
  draftTargetTypes: {
    RECIPE: 'Recipe / BOM',
    PROCESS_ROUTING: 'Process routing',
    DECLARATION: 'Declaration',
    ANALYSIS_PROFILE: 'Analysis profile',
    SPECIFICATION: 'Product specification',
    LABEL_TEMPLATE: 'Label template',
  },
  draftStatuses: {
    PROCESSING: 'Being created',
    CREATED: 'Revision created',
    FAILED: 'Creation failed',
  },
  relations: {
    SOURCE_PRODUCT: 'belongs to',
    OUTPUT_PRODUCT: 'produces',
    SOURCE_RECIPE: 'changes',
    USED_AS_COMPONENT: 'is used in',
    USED_AS_SUB_RECIPE: 'is a sub-recipe of',
    HAS_PROCESS_ROUTING: 'has routing',
    PLANNED_PRODUCTION: 'is planned in',
    MRP_PROPOSAL: 'has proposal',
    HAS_DECLARATION: 'has declaration',
    HAS_SPECIFICATION: 'has specification',
    LABEL_USED_FOR_PRODUCT: 'uses label',
    CUSTOMER_APPROVED_PRODUCT: 'is approved for',
    OPEN_SALES_ORDER: 'is ordered in',
    HAS_ANALYSIS: 'has analysis',
    HAS_PACKAGING: 'has packaging',
  },
  form: {
    newTitle: 'Create change request',
    newDescription: 'Record the source, intended state, risk and independent responsibilities.',
    editTitle: 'Edit change request',
    editDescription: 'Only drafts and returned requests can be edited.',
    title: 'Title',
    reason: 'Reason',
    sourceType: 'Source type',
    source: 'Source object',
    sourceVersion: 'Source version',
    sourceSearch: 'Find source',
    sourceSearchPlaceholder: 'Number, name, supplier or domain',
    selectSource: 'Select a controlled source',
    noSources: 'No matching source is available.',
    noVersion: 'Stable master data without a separate version',
    sourceIdPlaceholder: 'ID of the source object',
    optionalId: 'Optional version ID',
    selectProduct: 'Select product',
    risk: 'Risk level',
    riskAssessment: 'Risk assessment',
    before: 'Before',
    after: 'After',
    requestedEffectiveDate: 'Requested effective date',
    effectiveDate: 'Effective date',
    dueDate: 'Due date',
    responsibleId: 'Responsible person',
    reviewerId: 'Reviewer',
    approverId: 'Approver',
    selectUser: 'Select user',
    note: 'Reasoned note',
  },
  messages: {
    invalidJson: 'Before and after values must each be a valid JSON object.',
    invalidForm: 'Please complete all required fields with valid values.',
    noteRequired: 'Enter a reasoned note of at least three characters.',
    created: 'Change request {{number}} was created.',
    saved: 'Changes saved.',
    analyzed: 'Impact analysis saved.',
    notFound: 'The change request was not found.',
    workflow: {
      submit: 'Submitted for review.',
      reviewed: 'Review completed.',
      'changes-requested': 'Changes requested.',
      approve: 'Change request approved.',
      reject: 'Change request rejected.',
      'start-implementation': 'Implementation started.',
      complete: 'Implementation completed.',
      cancel: 'Change request cancelled.',
      archive: 'Change request archived.',
    },
  },
  sections: {
    overview: 'Overview',
    request: 'Request and risk',
    assignees: 'Responsibilities',
    diff: 'Before / after',
    impact: 'Impact graph',
    tasks: 'Tasks',
    drafts: 'Controlled revisions',
    documents: 'Documents',
    audit: 'Review and audit trail',
  },
  diff: {
    beforeDescription: 'Immutable baseline captured with the request.',
    afterDescription: 'Intended target state to be reviewed and approved.',
  },
  impact: {
    warnings: 'Impact-analysis warnings',
    relations: 'Relationships',
    noRelations: 'No relationships were identified.',
    openNode: 'Open {{label}}',
    requiredTitle: 'Current impact analysis required',
    requiredDescription: 'Analyse and save all affected objects before submitting this request.',
    notAnalyzed: 'No current impact analysis is available.',
    version: 'Impact snapshot v{{version}}, created {{date}}',
  },
  impactWarnings: {
    SUPPLIER_SOURCE_NOT_VERSIONED:
      'Supplier information has no separate version; the change record freezes its before/after values.',
    PACKAGING_SOURCE_NOT_VERSIONED:
      'Packaging master data has no separate version; the change record freezes its before/after values.',
    BOM_TRAVERSAL_DEPTH_REACHED: 'The BOM traversal reached its maximum depth of 50.',
    NO_DEPENDENCIES: 'No dependent objects were found for this source.',
  },
  tasks: {
    new: 'Add task',
    description: 'Assign a concrete implementation or review activity and due date.',
    created: 'Task created.',
    completedInUi: 'Completed in the change-control workflow.',
    complete: 'Complete',
    reopen: 'Reopen',
    columns: { status: 'Status', task: 'Task', assignee: 'Assignee', due: 'Due date' },
    form: { description: 'Description' },
  },
  drafts: {
    title: 'Published objects remain unchanged',
    description:
      'For every affected approved version, create a controlled new draft or revision. Existing published documents are never overwritten.',
    pending: 'No revision created yet',
    create: 'Create revision',
    created: 'A controlled draft or revision was created.',
  },
  workflow: {
    submit: {
      title: 'Submit change request',
      description: 'The impact snapshot is frozen for independent review.',
    },
    reviewed: {
      title: 'Complete review',
      description: 'Confirm that impact, risk and proposed state have been checked.',
    },
    'changes-requested': {
      title: 'Request changes',
      description: 'Explain precisely what must be corrected.',
    },
    approve: {
      title: 'Approve change request',
      description: 'Set the effective date and provide the independent approval note.',
    },
    reject: {
      title: 'Reject change request',
      description: 'Record why this request must not be implemented.',
    },
    'start-implementation': {
      title: 'Start implementation',
      description: 'Implementation tasks and controlled revisions can now be completed.',
    },
    complete: {
      title: 'Complete implementation',
      description: 'All tasks and required successor revisions must be complete.',
    },
    cancel: {
      title: 'Cancel change request',
      description: 'Cancellation is audit-logged and does not change published documents.',
    },
    archive: {
      title: 'Archive change request',
      description: 'The completed record and its evidence remain immutable.',
    },
  },
  empty: {
    requests: 'No change requests match the filters.',
    approvals: 'No customer approvals are available.',
    tasks: 'No tasks have been added.',
    drafts:
      'The current impact snapshot contains no approved object that requires a successor revision.',
    audit: 'No workflow decisions have been recorded.',
  },
  taskCount: '{{count}} open task(s)',
  pagination: 'Page {{page}} of {{pages}} · {{total}} change requests',
  customer: {
    actions: { new: 'New customer approval', approve: 'Approve', revoke: 'Revoke' },
    columns: {
      reference: 'Reference',
      customer: 'Customer',
      product: 'Product',
      scope: 'Approval scope',
      validity: 'Validity',
    },
    statuses: { DRAFT: 'Draft', APPROVED: 'Approved', EXPIRED: 'Expired', REVOKED: 'Revoked' },
    form: {
      title: 'Record customer approval',
      description:
        'The approved data is stored as a hash-protected snapshot and requires independent approval.',
      selectCustomer: 'Select customer',
      snapshot: 'Approved data snapshot (JSON)',
      validFrom: 'Valid from',
      validTo: 'Valid to',
    },
    dialog: {
      approve: 'Approve customer approval',
      revoke: 'Revoke customer approval',
      description: 'The decision and reason are written to the audit trail.',
    },
    messages: {
      created: 'Customer approval created.',
      approved: 'Customer approval approved.',
      revoked: 'Customer approval revoked.',
    },
  },
} as const;
